import React, { memo, useState, useEffect, useRef } from 'react';
import { useMobile } from '../../context';
import { useLang } from '../../i18n';
import { useDraft } from '../../hooks/useDraft';
import { keepInView } from '../shared/dom';
import { QtyPill } from '../shared/QtyPill';
import { fmtTime, fmtDate, fmtDateShort } from '../../logic/dates';
import { STATUS } from '../../config/lists';
import {
  SWIPE_MAX_PX, SWIPE_DELETE_THRESHOLD_PX, SWIPE_LOCK_PX, SWIPE_ANIM_MS, SWIPE_SETTLE_MS,
} from '../../config/constants';
import { RowMenu } from './RowMenu';
import { RowSheet } from './RowSheet';
import { rowState, doneVerb, markVerb } from './rowActions';

/**
 * One board item — the compact click row on desktop, the tap / swipe row on touch
 * screens. Memoized: `item` keeps its identity until its doc changes (data/rows.js) and
 * `actions` / `moveTargets` are one object for the whole board, so a tick re-renders
 * just that row.
 *
 *   item         the dailyRestockItems row
 *   list         its list (colour, sourceable, noQty)
 *   actions      makeItemActions(...) — setStatus / setDone / rename / setQty / setUrgent / move / remove
 *   moveTargets  [{ id, icon, color, label }] — every list on the board
 */
export const ItemRow = memo(function ItemRow(props) {
  return useMobile() ? <TouchItemRow {...props} /> : <DesktopItemRow {...props} />;
});

// The amount pill, with the Today board's wording.
function ItemQty({ item, actions }) {
  const { t } = useLang();
  return (
    <QtyPill cls="do-item" value={item.qty} onCommit={q => actions.setQty(item, q)}
      addLabel={t('today.addQty')} placeholder={t('qty.placeholder')} editAria={t('qty.setAria')}
      title={item.qty ? t('qty.editTitle', { qty: item.qty }) : t('qty.addTitle')}
      ariaLabel={item.qty ? t('qty.editAria', { qty: item.qty }) : t('qty.addAria')} />
  );
}

// Buy-lists tick ⇄ needed; task lists toggle done.
const toggleDone = (item, list, actions, refilled) => (list.sourceable
  ? actions.setStatus(item, refilled ? STATUS.NEEDED : STATUS.REFILLED)
  : actions.setDone(item, !item.done));

// ── Desktop / tablet row: ☐ name (tap to rename) · qty · status · ⋯ ─────────
function DesktopItemRow({ item, list, actions, moveTargets }) {
  const { t, ln } = useLang();
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useDraft(item.name, editing);
  const commitName = () => {
    const v = nameDraft.trim();
    setEditing(false);
    if (v && v !== item.name) actions.rename(item, v); else setNameDraft(item.name);
  };

  const { status, refilled, flagged, urgent, meta } = rowState(item);
  const markWord = markVerb(t, list);
  const doneTip = `${doneVerb(t, list)}${item.doneAt ? ` · ${fmtTime(item.doneAt)}` : ''}`;
  const flagTip = `${t(`status.${status}`)}${item.flaggedAt ? ` · ${fmtTime(item.flaggedAt)}` : ''}`;
  const origin = item.carriedFrom;   // kept from an earlier list

  return (
    <div className={`do-item${refilled ? ' do-item--done' : ''}${flagged ? ' do-item--flagged' : ''}${urgent ? ' do-item--urgent' : ''}`}
      style={{ '--cc': list.color, '--sc': meta.color }}>
      <button className={`do-item__check${refilled ? ' do-item__check--on' : ''}`}
        onClick={() => toggleDone(item, list, actions, refilled)}
        aria-label={refilled ? t('item.undoNeeded') : markWord} title={refilled ? doneTip : markWord}>
        <span className="do-item__check-box">{refilled ? '✓' : ''}</span>
      </button>
      {urgent && <span className="do-item__urg" title={t('item.urgent')}>★</span>}
      {editing ? (
        <input className="do-item__name-edit" value={nameDraft} autoFocus
          onChange={e => setNameDraft(e.target.value)} onBlur={commitName}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commitName(); }
            if (e.key === 'Escape') { setNameDraft(item.name); setEditing(false); }
          }}
          aria-label={t('item.editName', { name: item.name })} />
      ) : (
        <span className="do-item__name" onClick={() => setEditing(true)} title={t('item.tapToEdit')}>{ln(item.name)}</span>
      )}
      {origin && <span className="do-item__origin" title={t('item.noted', { date: fmtDate(origin) })}>{fmtDateShort(origin)}</span>}
      {!list.noQty && <ItemQty item={item} actions={actions} />}
      {(refilled || flagged) && (
        <span className="do-item__status" style={{ '--sc': meta.color }} title={refilled ? doneTip : flagTip}>{meta.icon}</span>
      )}
      <RowMenu item={item} list={list} actions={actions} moveTargets={moveTargets} />
    </div>
  );
}

// ── Touch row: tap = mark restocked / done, swipe left = delete, ⋯ = sheet ───
function TouchItemRow({ item, list, actions, moveTargets }) {
  const { t, ln } = useLang();
  const { status, refilled, flagged, urgent, meta } = rowState(item);

  // Rename (its own edit row)
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useDraft(item.name, editing);
  const saveEdit = () => {
    const n = nameDraft.trim();
    if (n && n !== item.name) actions.rename(item, n);
    setEditing(false);
  };
  const [menuOpen, setMenuOpen] = useState(false);

  // Swipe left to delete (vertical scroll passes through via touch-action: pan-y)
  const [dx, setDx] = useState(0);
  const [settling, setSettling] = useState(false);   // gliding back after a short swipe
  const [removing, setRemoving] = useState(false);
  useEffect(() => {
    if (!settling) return undefined;
    const id = setTimeout(() => setSettling(false), SWIPE_SETTLE_MS);
    return () => clearTimeout(id);
  }, [settling]);
  const start = useRef(null);
  const dir = useRef(null);
  const swiped = useRef(false);
  const onTouchStart = e => { const p = e.touches[0]; start.current = { x: p.clientX, y: p.clientY }; dir.current = null; swiped.current = false; };
  const onTouchMove = e => {
    if (!start.current) return;
    const p = e.touches[0];
    const ddx = p.clientX - start.current.x;
    const ddy = p.clientY - start.current.y;
    if (dir.current === null) {
      if (Math.abs(ddx) < SWIPE_LOCK_PX && Math.abs(ddy) < SWIPE_LOCK_PX) return;
      dir.current = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v';
    }
    if (dir.current !== 'h') return;              // vertical → let the page scroll
    swiped.current = true;
    setDx(Math.max(-SWIPE_MAX_PX, Math.min(0, ddx)));   // left only
  };
  const onTouchEnd = () => {
    if (dir.current === 'h' && dx <= -SWIPE_DELETE_THRESHOLD_PX) {
      setRemoving(true);
      setDx(-window.innerWidth);
      setTimeout(() => actions.remove(item), SWIPE_ANIM_MS);
    } else {
      if (dx !== 0) setSettling(true);
      setDx(0);
    }
    start.current = null; dir.current = null;
  };
  const onTap = () => { if (swiped.current) { swiped.current = false; return; } toggleDone(item, list, actions, refilled); };

  if (editing) {
    return (
      <div className="do-mrow do-mrow--edit" style={{ '--cc': list.color }}>
        <input className="do-item__name-edit" value={nameDraft} autoFocus onFocus={keepInView}
          onChange={e => setNameDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(false); }}
          aria-label={t('item.editName', { name: item.name })} />
        <button className="do-mrow__save" onClick={saveEdit}>{t('common.done')}</button>
      </div>
    );
  }

  const moving = dx !== 0 || settling;
  return (
    <>
      <div className={`do-mrow${removing ? ' do-mrow--removing' : ''}`} style={{ '--cc': list.color, '--sc': meta.color }}>
        <div className="do-mrow__action" aria-hidden>🗑 {t('menu.delete')}</div>
        {/* GPU layer only while a swipe is in progress — a resting row is plain paint. A
            permanent layer per row (with its rounded clip) is what blacked out the screen on
            the 24" kitchen tablet's weak GPU once the list got long. */}
        <div
          className={`do-mrow__fg${refilled ? ' do-mrow__fg--done' : ''}${flagged ? ' do-mrow__fg--flagged' : ''}${urgent ? ' do-mrow__fg--urgent' : ''}${moving ? ' do-mrow__fg--drag' : ''}`}
          style={moving ? { transform: `translateX(${dx}px)`, transition: start.current ? 'none' : `transform ${SWIPE_SETTLE_MS}ms ease` } : undefined}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        >
          <button className="do-mrow__toggle" onClick={onTap}
            aria-label={refilled ? t('item.markNeeded') : markVerb(t, list)}>
            <span className="do-mrow__dot">{refilled ? '✓' : flagged ? meta.icon : ''}</span>
            {urgent && <span className="do-item__urg">★</span>}
            <span className="do-mrow__name">{ln(item.name)}</span>
            {item.carriedFrom && <span className="do-item__origin">{fmtDateShort(item.carriedFrom)}</span>}
            {flagged && <span className="do-mrow__flag">{t(`status.${status}`)}</span>}
          </button>
          {!list.noQty && <ItemQty item={item} actions={actions} />}
          <button className="do-mrow__more" onClick={() => setMenuOpen(true)} aria-label={t('menu.more')}>⋯</button>
        </div>
      </div>
      {menuOpen && (
        <RowSheet item={item} list={list} actions={actions} moveTargets={moveTargets}
          onClose={() => setMenuOpen(false)} onRename={() => setEditing(true)} />
      )}
    </>
  );
}
